<template>
  <b-modal :id="modalId" :title="title" size="lg" @show="onShow">
    <div class="geo-editor-modal">
      <!-- Search / add a single country -->
      <b-form-group label="Add a country" class="mb-3">
        <b-input-group>
          <AutoComplete
            :input-attrs="{ class: 'form-control', placeholder: 'Search countries...' }"
            v-model="geo"
            :items="filteredGeos"
            :get-label="getName"
            :component-item="template"
            :min-len="0"
            @update-items="updateGeos"
            @item-selected="onItemSelected"
            @keyup.enter.native="addSelected"
          />
          <b-input-group-append>
            <b-button variant="primary" @click.stop="addSelected">Add</b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <!-- Region quick-add buttons -->
      <b-form-group label="Quick add regions" class="mb-3">
        <div class="region-row mb-2">
          <span class="region-row-label">Unions</span>
          <b-button
            v-for="r in politicalButtons"
            :key="r.key"
            size="sm"
            class="region-btn"
            :variant="r.stat.total && r.stat.selected === r.stat.total ? 'success' : 'outline-secondary'"
            :disabled="r.stat.total === 0"
            @click.stop="toggleRegion(r.codes)"
          >
            {{ r.label }}
            <b-badge v-if="r.stat.total" variant="light" class="ml-1">{{ r.stat.selected }}/{{ r.stat.total }}</b-badge>
          </b-button>
        </div>
        <div class="region-row">
          <span class="region-row-label">Continents</span>
          <b-button
            v-for="r in continentButtons"
            :key="r.key"
            size="sm"
            class="region-btn"
            :variant="r.stat.total && r.stat.selected === r.stat.total ? 'success' : 'outline-secondary'"
            :disabled="r.stat.total === 0"
            @click.stop="toggleRegion(r.codes)"
          >
            {{ r.label }}
            <b-badge v-if="r.stat.total" variant="light" class="ml-1">{{ r.stat.selected }}/{{ r.stat.total }}</b-badge>
          </b-button>
        </div>
      </b-form-group>

      <!-- Currently selected, grouped by continent -->
      <b-form-group :label="`Selected (${value.length})`" class="mb-0">
        <div class="selected-area">
          <p v-if="value.length === 0" class="text-muted mb-0">No countries selected — all locations allowed.</p>
          <div v-for="group in selectedGroups" :key="group.key" class="selected-group mb-2">
            <div class="selected-group-label">{{ group.label }}</div>
            <span class="geo-chip" v-for="item in group.items" :key="item.code">
              <b-badge variant="secondary">
                {{ item.name }}
                <button type="button" class="chip-remove" @click.stop="removeCode(item.code)" :aria-label="`Remove ${item.name}`">&times;</button>
              </b-badge>
            </span>
          </div>
        </div>
      </b-form-group>
    </div>

    <template #modal-footer>
      <b-button variant="outline-danger" size="sm" class="mr-auto" :disabled="value.length === 0" @click.stop="clearAll">
        Clear all
      </b-button>
      <b-button variant="primary" size="sm" @click.stop="hide">Done</b-button>
    </template>
  </b-modal>
</template>

<script>
import AutoComplete from "v-autocomplete"
import GeoTemplate from "./GeoTemplate.vue"
import { CONTINENTS, CONTINENT_ORDER, CONTINENT_BY_CODE, POLITICAL } from "../data/geoRegions"

export default {
  name: "GeoEditor",

  components: {
    AutoComplete
  },

  props: {
    modalId: { type: String, required: true },
    title: { type: String, default: "Edit Geo" },
    // All selectable geos as provided by the backend: [{ code, name }]
    geos: { type: Array, default: () => [] },
    // Currently selected country codes (v-model)
    value: { type: Array, default: () => [] }
  },

  data() {
    return {
      geo: { code: "", name: "" },
      filteredGeos: [],
      template: GeoTemplate
    }
  },

  computed: {
    // Codes the backend actually offers — region buttons only ever touch these.
    availableCodes() {
      return new Set(this.geos.map(g => g.code))
    },

    nameByCode() {
      return this.geos.reduce((acc, g) => { acc[g.code] = g.name; return acc }, {})
    },

    politicalButtons() {
      return Object.entries(POLITICAL).map(([key, r]) => this.buildRegionButton(key, r))
    },

    continentButtons() {
      return CONTINENT_ORDER.map(key => this.buildRegionButton(key, CONTINENTS[key]))
    },

    // Selected codes grouped by continent (plus an "Other" bucket for anything
    // not in our continent map), in display order.
    selectedGroups() {
      const buckets = {}
      this.value.forEach(code => {
        const key = CONTINENT_BY_CODE[code] || "other"
        if (!buckets[key]) buckets[key] = []
        buckets[key].push({ code, name: this.nameFor(code) })
      })
      const order = CONTINENT_ORDER.concat(["other"])
      return order
        .filter(key => buckets[key] && buckets[key].length)
        .map(key => ({
          key,
          label: key === "other" ? "Other" : CONTINENTS[key].label,
          items: buckets[key].sort((a, b) => a.name.localeCompare(b.name))
        }))
    }
  },

  watch: {
    geos() {
      this.refreshFiltered()
    }
  },

  methods: {
    onShow() {
      this.geo = { code: "", name: "" }
      this.refreshFiltered()
    },

    hide() {
      this.$bvModal.hide(this.modalId)
    },

    getName(geo) {
      return geo ? geo.name : ""
    },

    nameFor(code) {
      return this.nameByCode[code] || code
    },

    // Suggestions = available geos not already selected, optionally text-filtered.
    refreshFiltered(text) {
      const selected = new Set(this.value)
      let list = this.geos.filter(g => !selected.has(g.code))
      if (text) {
        const re = new RegExp(text.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        list = list.filter(g => re.test(g.name.toLowerCase()))
      }
      this.filteredGeos = list
    },

    updateGeos(text) {
      this.refreshFiltered(text)
    },

    onItemSelected(item) {
      this.geo = item
    },

    addSelected() {
      if (this.geo && this.geo.code) {
        this.addCode(this.geo.code)
        this.geo = { code: "", name: "" }
        this.refreshFiltered()
      }
    },

    addCode(code) {
      if (this.value.indexOf(code) === -1) {
        this.emitValue(this.value.concat(code))
      }
    },

    removeCode(code) {
      this.emitValue(this.value.filter(c => c !== code))
      this.refreshFiltered()
    },

    // Add all of the region's available codes, or remove them all if every one
    // is already selected (toggle behaviour).
    toggleRegion(codes) {
      const available = codes.filter(c => this.availableCodes.has(c))
      if (available.length === 0) return
      const selected = new Set(this.value)
      const allSelected = available.every(c => selected.has(c))
      if (allSelected) {
        const remove = new Set(available)
        this.emitValue(this.value.filter(c => !remove.has(c)))
      }
      else {
        const additions = available.filter(c => !selected.has(c))
        this.emitValue(this.value.concat(additions))
      }
      this.refreshFiltered()
    },

    clearAll() {
      this.emitValue([])
      this.refreshFiltered()
    },

    emitValue(arr) {
      this.$emit("input", arr)
    },

    buildRegionButton(key, region) {
      const available = region.codes.filter(c => this.availableCodes.has(c))
      const selectedSet = new Set(this.value)
      const selected = available.filter(c => selectedSet.has(c)).length
      return {
        key,
        label: region.label,
        codes: region.codes,
        stat: { selected, total: available.length }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.region-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.region-row-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.5);
  width: 5.5rem;
  flex-shrink: 0;
}

.region-btn {
  margin: 0;
}

.selected-area {
  max-height: 240px;
  overflow-y: auto;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2px;
}

.selected-group-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 3px;
}

.geo-chip {
  display: inline-block;
  margin: 0 4px 4px 0;

  .badge {
    font-size: 0.8rem;
    font-weight: normal;
    padding: 0.35em 0.5em;
  }
}

.chip-remove {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0 0 0 4px;
  font-size: 1rem;
  line-height: 1;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

// Keep the autocomplete suggestions above modal content. The modal itself sits
// at a high z-index (teleported to <body>), so this only needs to win locally.
::v-deep .v-autocomplete {
  position: relative;
  width: 100%;
}

::v-deep .v-autocomplete-list {
  position: absolute;
  background-color: #1B3B5B;
  width: 100%;
  text-align: left;
  border: 1px solid #80bdff;
  border-radius: 2px;
  max-height: 320px;
  overflow-y: auto;
  z-index: 1060;
}

::v-deep .v-autocomplete-item {
  padding: 4px 8px;
  cursor: pointer;
}

::v-deep .v-autocomplete-item-active {
  background-color: #005885 !important;
}
</style>
